import { CurrencyPipe, DatePipe, DecimalPipe, PercentPipe } from '@angular/common'
import { HttpParams } from '@angular/common/http'
import { Component } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { faSpinner } from '@fortawesome/free-solid-svg-icons'
import { WjGaugeModule } from '@grapecity/wijmo.angular2.gauge'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { NgbNav, NgbNavItem, NgbNavItemRole, NgbNavLink, NgbNavLinkBase } from '@ng-bootstrap/ng-bootstrap'

import { BaseComponent, BonusTableYearlyVariable, FeatureType, Grid, ListItem, ModalSize } from '../../shared'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { GridExportComponent } from '../../shared/reusable-components/grid-export/grid-export.component'
import { GridSelectionCalculationComponent } from '../../shared/reusable-components/grid-selection-calculation/grid-selection-calculation.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { BonusTableYearlyVariableComponent } from '../projections/bonus-table-yearly-variables/bonus-table-yearly-variable.component'

@Component({
    templateUrl: './bonus-table.component.html',
    standalone: true,
    imports: [
        NgbNav,
        NgbNavItem,
        NgbNavItemRole,
        NgbNavLink,
        NgbNavLinkBase,
        FormsModule,
        GridExportComponent,
        WjInputModule,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
        GridSelectionCalculationComponent,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        WjGaugeModule,
        DecimalPipe,
        CurrencyPipe,
        DatePipe,
    ],
})
export class BonusTableComponent extends BaseComponent {
    menuItems = [] as any[]
    currencyPipe = new CurrencyPipe('en-US')

    faSpinner = faSpinner
    active = 4
    bonusGrid: Grid<any>
    bonusInputGrid: Grid<BonusTableYearlyVariable>

    boolMap: ListItem<boolean>[] = [new ListItem(true, 'Show Active Jobs Only'), new ListItem(false, 'Show All Jobs')]
    activeJobsOnly = true
    yearMap: ListItem<number>[] = [new ListItem(2023, '2023'), new ListItem(2024, '2024'), new ListItem(2025, '2025')]
    selectedYear = 2023
    byJobByJobType: any = {}
    allDates: Date[] = []
    rawData: any[] = []
    allRevenueData: any[] = []
    availableYears: ListItem<number>[] = []
    currentYear = 0

    availableMonths: ListItem<number>[] = []

    // Gauge Properties
    progress = 0
    progress2 = 0
    ranges: number[] = [0, 0, 0, 0, 0, 0, 0, 0, 0]
    gaugeDate = new Date(new Date().getFullYear(), new Date().getMonth(), 1)
    currentYearTotalActualProfit = 0
    projectedProfit = 0
    nextYearProgress = 0
    nextYearGP = 0

    groupedPhaseData!: any

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.UsersEdit])
    }

    get frozenColumns(): number {
        return this.active != 3 ? 2 : 4
    }

    constructor() {
        super()

        this.bonusGrid = new Grid<any>(this.services, 'id', this).wrapColumnHeaders().isReadOnly(true).editOnDoubleClick(false)

        this.bonusInputGrid = new Grid<BonusTableYearlyVariable>(this.services, 'id', this)
            .setupModalEditing(
                this.services.data.bonusTableYearlyVariables,
                'Monthly Plan',
                BonusTableYearlyVariableComponent,
                () => new BonusTableYearlyVariable(),
                this,
                ModalSize.medium,
            )
            .wrapColumnHeaders()
        this.bonusInputGrid.modified.on(this.refreshBonus.bind(this))

        this.refresh()
    }

    refresh() {
        const params = new HttpParams().set('activeJobsOnly', this.activeJobsOnly)
        this.cancelableRequest(
            '',
            true,
            this.services.data
                .load(
                    true,
                    true,
                    this.services.data.get('/api/Projections/GetProjectionsData', params),
                    this.services.data.bonusTableYearlyVariables.queryAll(),
                    this.services.data.vpJobs.queryAll(),
                    this.services.data.get('/api/Projections/GetOpenMonth'),
                )
                .subscribe({
                    next: result => {
                        this.initialLoadingComplete()

                        this.rawData = result[0]
                        this.prepareData()
                        this.bonusInputGrid.items = this.services.data.bonusTableYearlyVariables.items

                        const years = this.services.data.bonusTableYearlyVariables.items.map(x => x.year)
                        const yearMap = [] as ListItem<number>[]
                        for (const year of years) {
                            yearMap.push(new ListItem(year, year.toString()))
                        }

                        this.yearMap = yearMap
                        this.refreshBonus()
                        this.calculateProgress()
                    },
                }),
        )
    }

    prepareData() {
        // Loop through the data and  set the binding (e.g. 'Jan 23') from the month date.
        const rawData = this.rawData
        for (const d of rawData) {
            d.binding = this.monthByInt.find(x => x.val == d.date.getMonth())?.display + ' ' + (d.date.getFullYear() - 2000)
        }

        this.allRevenueData = rawData

        // Let's get all the available years in the data.
        const dates = rawData.map(x => x.date).orderBy(x => x)
        let year = dates[0].getFullYear()
        const lastYear = dates[dates.length - 1].getFullYear()
        const availableYears = [] as ListItem<number>[]
        const currentYear = new Date().getFullYear()
        while (year <= lastYear) {
            const item = new ListItem(year, year)
            item['checked'] = false
            if (year == currentYear) {
                item['checked'] = true
            }
            availableYears.push(item)
            year++
        }
        this.availableYears = availableYears.orderByDescending(x => x.value)
    }

    // Bonus Table functions
    refreshBonus() {
        const inputs = this.services.data.bonusTableYearlyVariables.items.find(x => x.year == this.selectedYear) as BonusTableYearlyVariable

        if (!inputs) {
            this.services.notification.error(
                'No data found for the selected year. Please enter a record for the selected year in the Bonus Table Inputs tab.',
            )
            return
        }

        const metrics = [] as any[]
        const planPercent = inputs?.planPercent
        const bonusPoolPercent = inputs?.bonusPoolPercent
        const bonusExpensePercent = 0.19
        const percents = [0.9, 0.95, 1, 1.02]
        const overhead = inputs?.overhead
        const plan = inputs?.plan
        const extra = inputs?.extra
        const otherIncome = inputs?.otherIncome

        metrics.push({
            metric: 'Sales',
            plan90: plan,
            plan95: plan,
            plan: plan,
            level2: plan,
            level3: plan,
            level4: plan,
            level5: plan,
            level6: plan,
            level7: plan,
            level8: extra,
        })

        metrics.push({
            metric: 'Gross Margin',
            plan90: plan * planPercent * percents[0],
            plan95: plan * planPercent * percents[1],
            plan: plan * planPercent * percents[2],
            level2: plan * planPercent * percents[3],
            level3: plan * planPercent * percents[3] * percents[3],
            level4: plan * planPercent * percents[3] * percents[3] * percents[3],
            level5: plan * planPercent * percents[3] * percents[3] * percents[3] * percents[3],
            level6: plan * planPercent * percents[3] * percents[3] * percents[3] * percents[3] * percents[3],
            level7: plan * planPercent * percents[3] * percents[3] * percents[3] * percents[3] * percents[3] * percents[3],
            level8: extra * planPercent,
        })

        metrics.push({
            metric: 'Overhead',
            plan90: overhead,
            plan95: overhead,
            plan: overhead,
            level2: overhead,
            level3: overhead,
            level4: overhead,
            level5: overhead,
            level6: overhead,
            level7: overhead,
            level8: overhead,
        })

        metrics.push({
            metric: 'GPBT',
            plan90: metrics[1].plan90 - metrics[2].plan90,
            plan95: metrics[1].plan95 - metrics[2].plan95,
            plan: metrics[1].plan - metrics[2].plan,
            level2: metrics[1].level2 - metrics[2].level2,
            level3: metrics[1].level3 - metrics[2].level3,
            level4: metrics[1].level4 - metrics[2].level4,
            level5: metrics[1].level5 - metrics[2].level5,
            level6: metrics[1].level6 - metrics[2].level6,
            level7: metrics[1].level7 - metrics[2].level7,
            level8: metrics[1].level8 - metrics[2].level8,
        })

        metrics.push({
            metric: 'Total Bonus Pool',
            plan90: metrics[3].plan90 * (bonusPoolPercent - 0.01),
            plan95: metrics[3].plan95 * (bonusPoolPercent - 0.005),
            plan: metrics[3].plan * bonusPoolPercent,
            level2: metrics[3].level2 * (bonusPoolPercent + 0.005),
            level3: metrics[3].level3 * (bonusPoolPercent + 0.01),
            level4: metrics[3].level4 * (bonusPoolPercent + 0.015),
            level5: metrics[3].level5 * (bonusPoolPercent + 0.02),
            level6: metrics[3].level6 * (bonusPoolPercent + 0.025),
            level7: metrics[3].level7 * (bonusPoolPercent + 0.03),
            level8: metrics[3].level8 * (bonusPoolPercent + 0.04),
        })

        metrics.push({
            metric: '50% 401k MATCH',
            plan90: '-',
            plan95: '-',
            plan: '-',
            level2: '-',
            level3: '-',
            level4: '-',
            level5: '-',
            level6: '-',
            level7: '-',
            level8: '-',
        })

        metrics.push({
            metric: 'Bonus Pool',
            plan90: metrics[4].plan90,
            plan95: metrics[4].plan95,
            plan: metrics[4].plan,
            level2: metrics[4].level2,
            level3: metrics[4].level3,
            level4: metrics[4].level4,
            level5: metrics[4].level5,
            level6: metrics[4].level6,
            level7: metrics[4].level7,
            level8: metrics[4].level8,
        })

        const percentPipe = new PercentPipe('en-US')
        metrics.push({
            metric: 'Bonus Pool %',
            plan90: percentPipe.transform(bonusPoolPercent - 0.01),
            plan95: percentPipe.transform(bonusPoolPercent - 0.005),
            plan: percentPipe.transform(bonusPoolPercent),
            level2: percentPipe.transform(bonusPoolPercent + 0.005),
            level3: percentPipe.transform(bonusPoolPercent + 0.01),
            level4: percentPipe.transform(bonusPoolPercent + 0.015),
            level5: percentPipe.transform(bonusPoolPercent + 0.02),
            level6: percentPipe.transform(bonusPoolPercent + 0.025),
            level7: percentPipe.transform(bonusPoolPercent + 0.03),
            level8: percentPipe.transform(bonusPoolPercent + 0.04),
        })

        metrics.push({
            metric: 'Bonus Expenses',
            plan90: metrics[6].plan90 * bonusExpensePercent,
            plan95: metrics[6].plan95 * bonusExpensePercent,
            plan: metrics[6].plan * bonusExpensePercent,
            level2: metrics[6].level2 * bonusExpensePercent,
            level3: metrics[6].level3 * bonusExpensePercent,
            level4: metrics[6].level4 * bonusExpensePercent,
            level5: metrics[6].level5 * bonusExpensePercent,
            level6: metrics[6].level6 * bonusExpensePercent,
            level7: metrics[6].level7 * bonusExpensePercent,
            level8: metrics[6].level8 * bonusExpensePercent,
        })

        metrics.push({
            metric: 'Other Income',
            plan90: otherIncome,
            plan95: otherIncome,
            plan: otherIncome,
            level2: otherIncome,
            level3: otherIncome,
            level4: otherIncome,
            level5: otherIncome,
            level6: otherIncome,
            level7: otherIncome,
            level8: otherIncome,
        })

        metrics.push({
            metric: 'Net Profit (after bonus)',
            plan90: metrics[3].plan90 - metrics[6].plan90 - metrics[8].plan90 + metrics[9].plan90,
            plan95: metrics[3].plan95 - metrics[6].plan95 - metrics[8].plan95 + metrics[9].plan95,
            plan: metrics[3].plan - metrics[6].plan - metrics[8].plan + metrics[9].plan,
            level2: metrics[3].level2 - metrics[6].level2 - metrics[8].level2 + metrics[9].level2,
            level3: metrics[3].level3 - metrics[6].level3 - metrics[8].level3 + metrics[9].level3,
            level4: metrics[3].level4 - metrics[6].level4 - metrics[8].level4 + metrics[9].level4,
            level5: metrics[3].level5 - metrics[6].level5 - metrics[8].level5 + metrics[9].level5,
            level6: metrics[3].level6 - metrics[6].level6 - metrics[8].level6 + metrics[9].level6,
            level7: metrics[3].level7 - metrics[6].level7 - metrics[8].level7 + metrics[9].level7,
            level8: metrics[3].level8 - metrics[6].level8 - metrics[8].level8 + metrics[9].level8,
        })

        metrics.push({
            metric: 'Owner Distribution',
            plan90: '',
            plan95: '',
            plan: percentPipe.transform(0.18),
            level2: percentPipe.transform(0.19),
            level3: percentPipe.transform(0.19),
            level4: percentPipe.transform(0.2),
            level5: percentPipe.transform(0.21),
            level6: percentPipe.transform(0.22),
            level7: percentPipe.transform(0.23),
            level8: percentPipe.transform(0.24),
        })

        metrics.push({
            metric: 'NPBT',
            plan90: '',
            plan95: '',
            plan: '',
            level2: '',
            level3: '',
            level4: '',
            level5: '',
            level6: '',
            level7: '',
            level8: '',
        })

        metrics.push({
            metric: 'ROS',
            plan90: percentPipe.transform(metrics[10].plan90 / metrics[0].plan90, '0.2-2'),
            plan95: percentPipe.transform(metrics[10].plan95 / metrics[0].plan95, '0.2-2'),
            plan: percentPipe.transform(metrics[10].plan / metrics[0].plan, '0.2-2'),
            level2: percentPipe.transform(metrics[10].level2 / metrics[0].level2, '0.2-2'),
            level3: percentPipe.transform(metrics[10].level3 / metrics[0].level3, '0.2-2'),
            level4: percentPipe.transform(metrics[10].level4 / metrics[0].level4, '0.2-2'),
            level5: percentPipe.transform(metrics[10].level5 / metrics[0].level5, '0.2-2'),
            level6: percentPipe.transform(metrics[10].level6 / metrics[0].level6, '0.2-2'),
            level7: percentPipe.transform(metrics[10].level7 / metrics[0].level7, '0.2-2'),
            level8: percentPipe.transform(metrics[10].level8 / metrics[0].level8, '0.2-2'),
        })

        metrics.push({
            metric: 'GM%',
            plan90: percentPipe.transform(metrics[1].plan90 / metrics[0].plan90, '0.2-2'),
            plan95: percentPipe.transform(metrics[1].plan95 / metrics[0].plan95, '0.2-2'),
            plan: percentPipe.transform(metrics[1].plan / metrics[0].plan, '0.2-2'),
            level2: percentPipe.transform(metrics[1].level2 / metrics[0].level2, '0.2-2'),
            level3: percentPipe.transform(metrics[1].level3 / metrics[0].level3, '0.2-2'),
            level4: percentPipe.transform(metrics[1].level4 / metrics[0].level4, '0.2-2'),
            level5: percentPipe.transform(metrics[1].level5 / metrics[0].level5, '0.2-2'),
            level6: percentPipe.transform(metrics[1].level6 / metrics[0].level6, '0.2-2'),
            level7: percentPipe.transform(metrics[1].level7 / metrics[0].level7, '0.2-2'),
            level8: percentPipe.transform(metrics[1].level8 / metrics[0].level8, '0.2-2'),
        })

        this.bonusGrid.items = metrics
    }

    calculateProgress() {
        const currentYear = (this.currentYear = new Date().getFullYear())
        const yearMatch = this.availableYears.find(x => x['checked'] && x.value == currentYear)
        if (!yearMatch) {
            return
        }

        const thisYearDataForActuals = this.allRevenueData.filter(x => x.date.getFullYear() == currentYear && x.date < this.gaugeDate)
        const thisYearDataForProjections = this.allRevenueData.filter(x => x.date.getFullYear() == currentYear && x.date >= this.gaugeDate)

        const actualGrossProfits = thisYearDataForActuals.map(x => x.actualGrossProfit)
        this.currentYearTotalActualProfit = actualGrossProfits.length > 0 ? actualGrossProfits.reduce((a, b) => a + b) : 0
        this.projectedProfit = thisYearDataForProjections.map(x => x.projectedGrossProfit).reduce((a, b) => a + b)
        const actualPlusProjected = this.currentYearTotalActualProfit + this.projectedProfit

        const profitMetrics = this.bonusGrid.items.find(x => x.metric == 'Gross Margin')

        const btyv = this.services.data.bonusTableYearlyVariables.items.find(x => x.year == currentYear) as BonusTableYearlyVariable
        const goal = btyv.plan * btyv.planPercent

        this.progress = (this.currentYearTotalActualProfit / goal) * 100
        this.progress2 = (actualPlusProjected / goal) * 100

        this.ranges[0] = (profitMetrics.plan / profitMetrics.plan) * 100
        this.ranges[1] = (profitMetrics.level2 / profitMetrics.plan) * 100
        this.ranges[2] = (profitMetrics.level3 / profitMetrics.plan) * 100
        this.ranges[3] = (profitMetrics.level4 / profitMetrics.plan) * 100
        this.ranges[4] = (profitMetrics.level5 / profitMetrics.plan) * 100
        this.ranges[5] = (profitMetrics.level6 / profitMetrics.plan) * 100
        this.ranges[6] = (profitMetrics.level7 / profitMetrics.plan) * 100
        this.ranges[7] = (profitMetrics.level8 / profitMetrics.plan) * 100
        this.ranges[8] = (profitMetrics.level8 / profitMetrics.plan) * 100

        // Finally, we can do the next year projection
        const nextYearProjections = this.allRevenueData.filter(x => x.date.getFullYear() == currentYear + 1)
        const nextYearBtyv = this.services.data.bonusTableYearlyVariables.items.find(
            x => x.year == currentYear + 1,
        ) as BonusTableYearlyVariable
        if (!nextYearBtyv) return
        const nextYearGoal = nextYearBtyv.plan * nextYearBtyv.planPercent
        this.nextYearGP = nextYearProjections.map(x => x.projectedGrossProfit).reduce((a, b) => a + b)
        this.nextYearProgress = (this.nextYearGP / nextYearGoal) * 100
    }

    tabChanged() {
        setTimeout(() => {
            window.dispatchEvent(new Event('resize'))
        }, 50)
    }

    // Make it easy to lookup the month format we have chosen for our column binding (e.g. 'Jan 23')
    monthByInt = [
        { val: 0, display: 'Jan' },
        { val: 1, display: 'Feb' },
        { val: 2, display: 'Mar' },
        { val: 3, display: 'Apr' },
        { val: 4, display: 'May' },
        { val: 5, display: 'Jun' },
        { val: 6, display: 'Jul' },
        { val: 7, display: 'Aug' },
        { val: 8, display: 'Sep' },
        { val: 9, display: 'Oct' },
        { val: 10, display: 'Nov' },
        { val: 11, display: 'Dec' },
    ]
}
